/**
 * Care options derivation utilities.
 *
 * Extract selected care options from boolean fields.
 * All functions are SSR-safe pure functions.
 *
 * @module utils/derive-care-options
 */
import type { CareOptionKey } from "../types/care-seeker";

/**
 * Care options data structure from step 3 of the form.
 */
export interface CareOptionsData {
  mobilityAssistance: boolean;
  everydayTasks: boolean;
  memoryCare: boolean;
  other: boolean;
}

/**
 * Convert boolean care options to an array of selected option keys.
 *
 * This is useful for analytics tracking, display purposes, and API payloads
 * that expect an array of selected options rather than individual booleans.
 *
 * @param careOptions - The boolean care options from step 3
 * @returns Array of selected care option keys
 *
 * @example
 * ```ts
 * const options = {
 *   mobilityAssistance: true,
 *   everydayTasks: true,
 *   memoryCare: false,
 *   other: false,
 * };
 *
 * const selected = deriveCareOptions(options);
 * // Returns: ["mobilityAssistance", "everydayTasks"]
 * ```
 */
export function deriveCareOptions(
  careOptions: CareOptionsData,
): CareOptionKey[] {
  const keys: CareOptionKey[] = [
    "mobilityAssistance",
    "everydayTasks",
    "memoryCare",
    "other",
  ];

  return keys.filter((key) => careOptions[key] === true);
}
